import { Outlet, useNavigate, useParams } from 'react-router-dom'

import { TeamDocument, TeamDetailFragment } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import {
  ResponsivePage,
  type ResponsivePageActionsInterface,
} from 'modules/layout/components/ResponsivePage'
import TeamDetail from 'modules/teams/components/Detail'
import EditModal from 'modules/teams/components/EditModal'
import TeamsMembersModal from 'modules/teams/components/MembersModal'
import RemoveTeamModal from 'modules/teams/components/RemoveTeamModal'
import type { ModalsState } from 'modules/teams/hooks/useModals'
import useModals from 'modules/teams/hooks/useModals'

interface ContentProps {
  team: TeamDetailFragment
  canManageTeams: boolean
  canCreateJob: boolean
  modals: ModalsState
  hasDevJobCatalogTree: boolean
}

const Content = ({
  team,
  canManageTeams,
  canCreateJob,
  modals,
  hasDevJobCatalogTree,
}: ContentProps) => {
  if (!team) return null

  return (
    <>
      {modals.isAddMemberOpen && (
        <TeamsMembersModal team={team} onClose={modals.onCloseAddMember} />
      )}

      {modals.isEditOpen && <EditModal team={team} onClose={modals.onCloseEdit} />}

      {modals.isRemoveOpen && <RemoveTeamModal team={team} onClose={modals.onCloseRemove} />}

      <TeamDetail
        readonly={!canManageTeams}
        team={team}
        hasDevJobCatalogTree={hasDevJobCatalogTree}
        canCreateJob={canCreateJob}
      />
    </>
  )
}

const TeamDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const { company } = useCurrentSession()
  const { data, loading: isLoadingTeam } = useQuery(TeamDocument, {
    variables: {
      teamId: String(id),
      companyId: company.id,
    },
    fetchPolicy: 'cache-first',
  })
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const modals = useModals()
  const team = data?.teams.team
  const canManageTeams = data?.teams.team?._policies?.manage?.can ?? false
  const canCreateJob = data?.jobCatalog._policies?.createJob?.can ?? false
  const hasLegacyJobCatalog =
    data?.features.featuresConnection.nodes.find((feature) => feature.name === 'legacy_job_catalog')
      ?.enabled ?? false
  const hasDevJobCatalogTree = !hasLegacyJobCatalog

  const actions: ResponsivePageActionsInterface = {
    primary: [
      {
        icon: 'add',
        label: i18n.t('teams.members_modal.title'),
        onClick: modals.onOpenAddMember,
        variant: 'primary',
      },
    ],
    secondary: [
      { onClick: modals.onOpenEdit, title: i18n.t('teams.edit') },
      {
        onClick: modals.onOpenRemove,
        title: i18n.t('teams.delete'),
        danger: true,
      },
    ],
  }

  // todo: passs down title and description error to responsive page
  // title: i18n.t('errors.page_not_found.title'),
  // description: i18n.t('errors.page_not_found.description')

  return (
    <ResponsivePage.Body
      showHeader={false}
      actions={canManageTeams ? actions : undefined}
      loading={isLoadingTeam}
      error={!team}
      onActionClick={(key) => {
        if (key === 'add-team') {
          return (
            team && navigate(navigation.employees.teams.id.addMembers.fullPath({ id: team.id }))
          )
        } else if (key === 'edit-team') {
          return team && navigate(navigation.employees.teams.id.edit.fullPath({ id: team.id }))
        } else if (key === 'delete-team') {
          return team && navigate(navigation.employees.teams.id.delete.fullPath({ id: team.id }))
        }
      }}
    >
      {!!team && (
        <Content
          canManageTeams={canManageTeams}
          canCreateJob={canCreateJob}
          hasDevJobCatalogTree={hasDevJobCatalogTree}
          team={team}
          modals={modals}
        />
      )}
      <Outlet />
    </ResponsivePage.Body>
  )
}

export default TeamDetailPage
